import { KNOWN_SERVICES, FALLBACK_HUES } from '$data/known-services';

/** FNV-1a 32-bit hash (deterministic, fast, no deps). */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export interface Identity {
  hue: number;
  sat: number;
  lit: number;
  monogram: string;
  /** CSS color string */
  bg: string;
  /** Best-contrast text color for `bg` */
  fg: string;
  /** Was this issuer found in KNOWN_SERVICES? */
  known: boolean;
}

const _identityCache = new Map<string, Identity>();

export function resolveIdentity(issuer: string): Identity {
  const norm = issuer.toLowerCase().trim();
  const cached = _identityCache.get(norm);
  if (cached) return cached;
  let color = KNOWN_SERVICES[norm];
  let known = !!color;

  if (!color) {
    // Substring scan for partial matches ("GitHub Enterprise" → github)
    for (const k in KNOWN_SERVICES) {
      if (norm.includes(k)) {
        color = KNOWN_SERVICES[k];
        known = true;
        break;
      }
    }
  }

  if (!color) {
    const idx = fnv1a(norm) % FALLBACK_HUES.length;
    color = { hue: FALLBACK_HUES[idx]!, sat: 70, lit: 60 };
    known = false;
  }

  const monogram = (issuer.trim().charAt(0) || '?').toUpperCase();
  const bg = `hsl(${color.hue}, ${color.sat}%, ${color.lit}%)`;
  const fg = color.lit > 55 ? '#000000' : '#ffffff';

  const result: Identity = { ...color, monogram, bg, fg, known };
  _identityCache.set(norm, result);
  return result;
}

/**
 * Detect a known service from a URL or string.
 * Returns the canonical KNOWN_SERVICES key, or null.
 */
export function detectService(input: string): string | null {
  const lower = input.toLowerCase();
  // Try host extraction
  let host = lower;
  try {
    if (lower.startsWith('http')) {
      host = new URL(lower).hostname;
    }
  } catch {
    /* not a URL */
  }
  // Strip www. and TLD-ish tail
  const cleaned = host.replace(/^www\./, '').split('.')[0] ?? host;
  for (const k in KNOWN_SERVICES) {
    if (cleaned.includes(k) || lower.includes(k)) return k;
  }
  return null;
}
